import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/data/site";
import { FadeUp, Chapter, RevealLines, EASE } from "./motion";

const VIZ_STYLES = ["Modern", "Luxury", "Minimal", "Scandinavian", "Contemporary"];

export default function Visualizer() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [style, setStyle] = useState("Luxury");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const generate = async () => {
    if (!file) {
      toast.error("Upload a photo of your room first");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("style", style);
      fd.append("file", file);
      const res = await fetch(`${API}/visualize`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Generation failed");
      const data = await res.json();
      if (!data.image) throw new Error("No image returned — try another photo");
      setResult(data.image);
      toast.success("Your redesigned room is ready");
    } catch (e) {
      toast.error(e.message || "Visualization failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="visualizer" data-testid="visualizer-section" className="grain relative overflow-hidden bg-[#1B1B1B] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Chapter num="11" title="AI Room Visualizer" dark />
        <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
          <RevealLines
            className="font-display text-4xl tracking-tight text-[#F7F5F2] md:text-6xl"
            lines={[<>See your room,</>, <><span className="italic text-[#B68D40]">reimagined</span> by AI.</>]}
          />
          <FadeUp delay={0.3}>
            <p className="max-w-sm text-sm leading-relaxed text-[#F7F5F2]/60">
              Upload a photo of any room and watch our AI restyle it — a glimpse of what our designers will perfect in person.
            </p>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <FadeUp>
            <div
              onClick={() => inputRef.current?.click()}
              data-testid="visualizer-upload"
              className="group relative flex h-[320px] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-dashed border-[#F7F5F2]/25 transition-colors duration-300 hover:border-[#B68D40] md:h-[420px]"
            >
              {preview ? (
                <img src={preview} alt="Your uploaded room" className="h-full w-full object-cover" />
              ) : (
                <div className="text-center">
                  <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#B68D40]/40 text-[#B68D40] transition-transform duration-500 group-hover:scale-110">
                    <Upload size={22} />
                  </span>
                  <p className="mt-5 font-display text-xl text-[#F7F5F2]">Upload your room</p>
                  <p className="mt-2 text-xs text-[#F7F5F2]/50">JPG or PNG · living room, bedroom, kitchen…</p>
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="visualizer-file-input"
                onChange={(e) => pick(e.target.files?.[0])}
              />
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {VIZ_STYLES.map((s) => (
                <button
                  key={s}
                  onClick={() => setStyle(s)}
                  data-testid={`viz-style-${s.toLowerCase()}`}
                  className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-300 ${
                    style === s ? "bg-[#B68D40] text-[#1B1B1B]" : "border border-[#F7F5F2]/20 text-[#F7F5F2]/70 hover:border-[#B68D40] hover:text-[#B68D40]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <button
              onClick={generate}
              disabled={loading}
              data-testid="visualizer-generate-btn"
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#F7F5F2] py-4 text-sm font-semibold uppercase tracking-widest text-[#1B1B1B] transition-colors duration-300 hover:bg-[#B68D40] disabled:opacity-50"
            >
              <Sparkles size={16} />
              {loading ? "Crafting your space…" : `Visualize in ${style} Style`}
            </button>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div
              data-testid="visualizer-result"
              className="relative flex h-[320px] items-center justify-center overflow-hidden rounded-2xl border border-[#F7F5F2]/10 bg-[#F7F5F2]/5 md:h-[420px] lg:h-full"
            >
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center">
                    <span className="animate-spin-slow mx-auto block h-14 w-14 rounded-full border border-[#F7F5F2]/10 border-t-[#B68D40]" />
                    <p className="mt-6 font-serif2 text-lg italic text-[#F7F5F2]/80">Sketching light, shadow & silk…</p>
                    <p className="mt-2 font-num text-[10px] uppercase tracking-[0.3em] text-[#F7F5F2]/40">This takes 15–30 seconds</p>
                  </motion.div>
                ) : result ? (
                  <motion.div key="result" className="h-full w-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <motion.img
                      src={result}
                      alt={`AI visualized ${style} interior`}
                      className="h-full w-full object-cover"
                      initial={{ clipPath: "inset(0 100% 0 0)" }}
                      animate={{ clipPath: "inset(0 0% 0 0)" }}
                      transition={{ duration: 1.2, ease: EASE }}
                    />
                    <span className="absolute left-5 top-5 rounded-full bg-[#B68D40] px-4 py-1.5 font-num text-[10px] uppercase tracking-[0.3em] text-[#1B1B1B]">
                      {style} · AI Concept
                    </span>
                    <button
                      onClick={() => { setResult(null); setFile(null); setPreview(null); }}
                      data-testid="visualizer-reset-btn"
                      className="glass-dark absolute bottom-5 right-5 flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold text-[#F7F5F2] transition-colors hover:text-[#B68D40]"
                    >
                      <RotateCcw size={13} /> Try another
                    </button>
                  </motion.div>
                ) : (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-10 text-center">
                    <p className="font-display text-2xl italic text-[#F7F5F2]/40">Your redesigned room appears here</p>
                    <p className="mt-3 text-xs text-[#F7F5F2]/30">Powered by Gemini image intelligence</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
